import { useState, useEffect, useMemo } from 'react';
import {
  getAgentLeaderboard,
  getAgentDispositionBreakdown,
  getAgentProducts,
  getAgentResponseTime,
  getAgentProductMatrix,
} from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import {
  Trophy, Users, ShoppingCart, TrendingUp, Clock, Package,
  ChevronUp, ChevronDown, ArrowUpDown, Banknote, RefreshCw,
  Medal,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ─── Constants ───────────────────────────────────────────────────────────────

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#3b82f6',
  '#ef4444', '#a855f7', '#06b6d4', '#84cc16',
];

const RANK_COLORS = ['#f59e0b', '#9ca3af', '#b45309'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaderboardRow {
  user_id: number;
  user_uuid: string;
  agent_name: string;
  agent_email: string;
  leads_assigned: number;
  order_count: number;
  total_revenue: number;
  paid_revenue: number;
  total_units: number;
  cod_orders: number;
  partial_orders: number;
  prepaid_orders: number;
  conversion_pct: number;
  aov: number;
}

interface DispositionRow {
  user_id: number;
  agent_name: string;
  l1_label: string;
  l2_label: string | null;
  l3_label: string | null;
  disposition: string;
  lead_count: number;
}

interface ProductMatrixAgent {
  user_id: number;
  user_uuid: string;
  agent_name: string;
  products: { product_title: string; pack_type: string; total_units: number; total_revenue: number; rank: number }[];
}

interface ProductRow {
  product_title: string;
  pack_type: string;
  total_units: number;
  total_revenue: number;
  order_count: number;
}

interface ResponseRow {
  user_id: number;
  agent_name: string;
  total_leads: number;
  leads_responded: number;
  leads_no_response: number;
  avg_response_mins: number | null;
  min_response_mins: number | null;
  max_response_mins: number | null;
}

type SortDir = 'asc' | 'desc';
type TabId = 'leaderboard' | 'disposition' | 'products' | 'response';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
  `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const fmtMins = (v: number | null) => {
  if (v === null || v === undefined) return '—';
  if (v < 60) return `${Math.round(v)}m`;
  const h = Math.floor(v / 60);
  const m = Math.round(v % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const responseColor = (mins: number | null) => {
  if (!mins) return 'text-gray-400 dark:text-gray-500';
  if (mins < 30) return 'text-green-600 dark:text-green-400';
  if (mins < 120) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const responseBg = (mins: number | null) => {
  if (!mins) return '#94a3b8';
  if (mins < 30) return '#22c55e';
  if (mins < 120) return '#eab308';
  return '#ef4444';
};

const truncate = (s: string, n: number) => s.length > n ? s.slice(0, n - 1) + '…' : s;

// ─── Component ────────────────────────────────────────────────────────────────

const AgentPerformancePage = () => {
  // Filters
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [leadBucket, setLeadBucket] = useState('');

  // Tabs
  const [activeTab, setActiveTab] = useState<TabId>('leaderboard');

  // Leaderboard sort
  const [sortCol, setSortCol] = useState('order_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Disposition accordion
  const [expandedAgentId, setExpandedAgentId] = useState<number | null>(null);

  // Products
  const [productMode, setProductMode] = useState<'units' | 'revenue'>('units');
  const [productAgentUuid, setProductAgentUuid] = useState('');

  // Data
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [disposition, setDisposition] = useState<DispositionRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [productMatrix, setProductMatrix] = useState<ProductMatrixAgent[]>([]);
  const [responseTime, setResponseTime] = useState<ResponseRow[]>([]);

  // Per-tab loading & cache
  const [tabLoading, setTabLoading] = useState<Partial<Record<TabId, boolean>>>({});
  const [loaded, setLoaded] = useState<Set<TabId>>(new Set());

  const baseReq = () => ({
    from_date: fromDate,
    to_date: toDate,
    lead_bucket: leadBucket || undefined,
  });

  const fetchTab = async (tab: TabId, force = false) => {
    if (!force && loaded.has(tab)) return;
    setTabLoading(prev => ({ ...prev, [tab]: true }));
    try {
      if (tab === 'leaderboard') {
        const res = await getAgentLeaderboard(baseReq());
        if (res.success) setLeaderboard(res.data as LeaderboardRow[]);
      } else if (tab === 'disposition') {
        const res = await getAgentDispositionBreakdown(baseReq());
        if (res.success) setDisposition(res.data as DispositionRow[]);
      } else if (tab === 'products') {
        const [resProducts, resMatrix] = await Promise.all([
          getAgentProducts({ ...baseReq(), agent_uuid: productAgentUuid || undefined }),
          getAgentProductMatrix(baseReq()),
        ]);
        if (resProducts.success) setProducts(resProducts.data as ProductRow[]);
        if (resMatrix.success) setProductMatrix(resMatrix.data as ProductMatrixAgent[]);
      } else if (tab === 'response') {
        const res = await getAgentResponseTime(baseReq());
        if (res.success) setResponseTime(res.data as ResponseRow[]);
      }
      setLoaded(prev => new Set(prev).add(tab));
    } catch (e: any) {
      toastMessage(e.message || 'Error fetching data', 'error');
    } finally {
      setTabLoading(prev => ({ ...prev, [tab]: false }));
    }
  };

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    fetchTab(tab);
  };

  const handleRefresh = () => {
    setLoaded(new Set()); // clear all cache
    fetchTab(activeTab, true); // force re-fetch only active tab
  };

  useEffect(() => { fetchTab('leaderboard'); }, []);

  // Re-fetch products when agent filter changes (only if products tab was visited)
  useEffect(() => {
    if (loaded.has('products')) fetchTab('products', true);
  }, [productAgentUuid]);

  // ── Computed ───────────────────────────────────────────────────────────────

  const summary = useMemo(() => {
    const totalLeads = leaderboard.reduce((s, r) => s + r.leads_assigned, 0);
    const totalOrders = leaderboard.reduce((s, r) => s + r.order_count, 0);
    const totalRevenue = leaderboard.reduce((s, r) => s + r.total_revenue, 0);
    const totalUnits = leaderboard.reduce((s, r) => s + r.total_units, 0);
    const avgConv = leaderboard.length
      ? +(leaderboard.reduce((s, r) => s + r.conversion_pct, 0) / leaderboard.length).toFixed(1)
      : 0;
    return { totalAgents: leaderboard.length, totalLeads, totalOrders, totalRevenue, totalUnits, avgConv };
  }, [leaderboard]);

  const sortedLeaderboard = useMemo(() => {
    return [...leaderboard].sort((a, b) => {
      const av = (a as any)[sortCol] ?? 0;
      const bv = (b as any)[sortCol] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [leaderboard, sortCol, sortDir]);

  const maxOrders = useMemo(() => Math.max(...leaderboard.map(r => r.order_count), 1), [leaderboard]);

  // Disposition: per-agent table (grouped)
  const agentDispositionTable = useMemo(() => {
    const agentIds = [...new Set(disposition.map(r => r.user_id))];
    return agentIds.map(id => {
      const rows = disposition.filter(r => r.user_id === id);
      const total = rows.reduce((s, r) => s + r.lead_count, 0);
      return {
        user_id: id,
        agent_name: rows[0]?.agent_name ?? '',
        total,
        rows: rows.map((r, i) => ({ ...r, colorIdx: i })),
      };
    });
  }, [disposition]);

  // Auto-open first agent in accordion when disposition data loads
  useEffect(() => {
    if (agentDispositionTable.length > 0) {
      setExpandedAgentId(agentDispositionTable[0].user_id);
    }
  }, [agentDispositionTable]);

  // Products: sorted top 10
  const top10Products = useMemo(() => {
    return [...products]
      .sort((a, b) => productMode === 'units'
        ? b.total_units - a.total_units
        : b.total_revenue - a.total_revenue)
      .slice(0, 10);
  }, [products, productMode]);

  const productsChartData = useMemo(() => ({
    labels: top10Products.map(p => {
      const label = p.pack_type !== 'Default' ? `${p.product_title} (${p.pack_type})` : p.product_title;
      return truncate(label, 36);
    }),
    datasets: [{
      label: productMode === 'units' ? 'Units Sold' : 'Revenue (₹)',
      data: top10Products.map(p => productMode === 'units' ? p.total_units : Math.round(p.total_revenue)),
      backgroundColor: PALETTE,
      borderRadius: 4,
    }],
  }), [top10Products, productMode]);

  // Response time
  const sortedResponse = useMemo(() =>
    [...responseTime].sort((a, b) => (a.avg_response_mins ?? Infinity) - (b.avg_response_mins ?? Infinity)),
    [responseTime]
  );

  const responseChartData = useMemo(() => ({
    labels: sortedResponse.map(r => r.agent_name),
    datasets: [{
      label: 'Avg Response (mins)',
      data: sortedResponse.map(r => r.avg_response_mins ?? 0),
      backgroundColor: sortedResponse.map(r => responseBg(r.avg_response_mins)),
      borderRadius: 4,
    }],
  }), [sortedResponse]);

  const avgResponseAll = useMemo(() => {
    const valid = responseTime.filter(r => r.avg_response_mins !== null);
    if (!valid.length) return null;
    return +(valid.reduce((s, r) => s + r.avg_response_mins!, 0) / valid.length).toFixed(1);
  }, [responseTime]);

  const respondedPct = useMemo(() => {
    const totalLeads = responseTime.reduce((s, r) => s + r.total_leads, 0);
    const totalResponded = responseTime.reduce((s, r) => s + r.leads_responded, 0);
    return totalLeads > 0 ? +((totalResponded / totalLeads) * 100).toFixed(1) : 0;
  }, [responseTime]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 opacity-30 ml-1 inline" />;
    return sortDir === 'desc'
      ? <ChevronDown className="h-3 w-3 ml-1 inline text-brandColor" />
      : <ChevronUp className="h-3 w-3 ml-1 inline text-brandColor" />;
  };

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'leaderboard', label: 'Leaderboard', icon: <Trophy className="h-4 w-4" /> },
    { id: 'disposition', label: 'Dispositions', icon: <Users className="h-4 w-4" /> },
    { id: 'products', label: 'Products', icon: <Package className="h-4 w-4" /> },
    { id: 'response', label: 'Response Time', icon: <Clock className="h-4 w-4" /> },
  ];

  const kpiCards = [
    { label: 'Active Agents', value: summary.totalAgents.toString(), icon: <Users className="h-5 w-5" />, from: 'from-blue-500', to: 'to-blue-700' },
    { label: 'Total Leads', value: summary.totalLeads.toLocaleString(), icon: <TrendingUp className="h-5 w-5" />, from: 'from-violet-500', to: 'to-violet-700' },
    { label: 'Total Orders', value: summary.totalOrders.toLocaleString(), icon: <ShoppingCart className="h-5 w-5" />, from: 'from-indigo-500', to: 'to-indigo-700' },
    { label: 'Avg Conversion', value: `${summary.avgConv}%`, icon: <TrendingUp className="h-5 w-5" />, from: 'from-emerald-500', to: 'to-emerald-700' },
    { label: 'Total Revenue', value: fmt(summary.totalRevenue), icon: <Banknote className="h-5 w-5" />, from: 'from-orange-500', to: 'to-orange-600' },
    { label: 'Total Units', value: summary.totalUnits.toLocaleString(), icon: <Package className="h-5 w-5" />, from: 'from-teal-500', to: 'to-teal-700' },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-950">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Performance</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">CRM leads, conversions & order metrics per agent</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={leadBucket}
              onChange={e => setLeadBucket(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Buckets</option>
              <option value="regular">Regular Leads</option>
              <option value="abandoned_cart">Abandoned Cart</option>
              <option value="reorder">Reorder</option>
            </select>
            <input
              type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handleRefresh} disabled={!!tabLoading[activeTab]}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brandColor text-white text-sm rounded-lg hover:bg-brandColor/90 disabled:opacity-50 font-medium transition-all"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${tabLoading[activeTab] ? 'animate-spin' : ''}`} />
              {tabLoading[activeTab] ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">

        {/* ── KPI Cards ────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          {kpiCards.map(card => (
            <div
              key={card.label}
              className={`bg-gradient-to-br ${card.from} ${card.to} rounded-xl p-4 text-white shadow-sm`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium opacity-80">{card.label}</p>
                <div className="opacity-70">{card.icon}</div>
              </div>
              <p className="text-xl font-bold tracking-tight">{card.value}</p>
            </div>
          ))}
        </div>

        {/* ── Tab Nav ──────────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-all border-b-2
                  ${activeTab === tab.id
                    ? 'border-brandColor text-brandColor dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'}
                `}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Leaderboard ──────────────────────────────────────────────── */}
          {activeTab === 'leaderboard' && (
            <div>
              {tabLoading['leaderboard'] ? (
                <div className="flex items-center justify-center h-48">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor" />
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading leaderboard…</p>
                  </div>
                </div>
              ) : sortedLeaderboard.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400 dark:text-gray-500">
                  No agent data found for the selected period.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 w-10">#</th>
                        <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Agent</th>
                        <th
                          className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white"
                          onClick={() => handleSort('leads_assigned')}
                        >
                          Leads <SortIcon col="leads_assigned" />
                        </th>
                        <th
                          className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white min-w-[160px]"
                          onClick={() => handleSort('order_count')}
                        >
                          Orders <SortIcon col="order_count" />
                        </th>
                        <th
                          className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white"
                          onClick={() => handleSort('conversion_pct')}
                        >
                          Conv% <SortIcon col="conversion_pct" />
                        </th>
                        <th
                          className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white"
                          onClick={() => handleSort('total_revenue')}
                        >
                          Revenue <SortIcon col="total_revenue" />
                        </th>
                        <th
                          className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white"
                          onClick={() => handleSort('aov')}
                        >
                          AOV <SortIcon col="aov" />
                        </th>
                        <th
                          className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white"
                          onClick={() => handleSort('total_units')}
                        >
                          Units <SortIcon col="total_units" />
                        </th>
                        <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 min-w-[180px]">
                          Payment Split
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {sortedLeaderboard.map((row, idx) => {
                        const rank = idx + 1;
                        const orderPct = Math.round((row.order_count / maxOrders) * 100);
                        const convColor =
                          row.conversion_pct >= 20 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : row.conversion_pct >= 10 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                        const totalPayment = row.prepaid_orders + row.cod_orders + row.partial_orders;

                        return (
                          <tr key={row.user_id} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors">
                            {/* Rank */}
                            <td className="px-4 py-3.5">
                              {rank <= 3 ? (
                                <Medal className="h-5 w-5" style={{ color: RANK_COLORS[rank - 1] }} />
                              ) : (
                                <span className="text-xs text-gray-400 font-mono">{rank}</span>
                              )}
                            </td>
                            {/* Agent */}
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-2.5">
                                <div
                                  className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                  style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}
                                >
                                  {row.agent_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white text-sm">{row.agent_name}</p>
                                  {row.agent_email && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500">{row.agent_email}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            {/* Leads */}
                            <td className="px-4 py-3.5 text-right text-gray-700 dark:text-gray-300 font-mono text-sm">
                              {row.leads_assigned.toLocaleString()}
                            </td>
                            {/* Orders + bar */}
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm text-gray-900 dark:text-white w-8 text-right flex-shrink-0">
                                  {row.order_count}
                                </span>
                                <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-brandColor transition-all"
                                    style={{ width: `${orderPct}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            {/* Conversion */}
                            <td className="px-4 py-3.5 text-right">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${convColor}`}>
                                {row.conversion_pct}%
                              </span>
                            </td>
                            {/* Revenue */}
                            <td className="px-4 py-3.5 text-right text-gray-900 dark:text-white font-medium text-sm">
                              {fmt(row.total_revenue)}
                            </td>
                            {/* AOV */}
                            <td className="px-4 py-3.5 text-right text-gray-700 dark:text-gray-300 text-sm">
                              {fmt(row.aov)}
                            </td>
                            {/* Units */}
                            <td className="px-4 py-3.5 text-right text-gray-700 dark:text-gray-300 font-mono text-sm">
                              {row.total_units.toLocaleString()}
                            </td>
                            {/* Payment split */}
                            <td className="px-4 py-3.5">
                              {totalPayment > 0 ? (
                                <div className="space-y-1">
                                  {/* Mini stacked bar */}
                                  <div className="flex h-2 rounded-full overflow-hidden gap-px">
                                    {row.prepaid_orders > 0 && (
                                      <div
                                        className="bg-green-500"
                                        style={{ width: `${(row.prepaid_orders / totalPayment) * 100}%` }}
                                        title={`Prepaid: ${row.prepaid_orders}`}
                                      />
                                    )}
                                    {row.cod_orders > 0 && (
                                      <div
                                        className="bg-orange-400"
                                        style={{ width: `${(row.cod_orders / totalPayment) * 100}%` }}
                                        title={`COD: ${row.cod_orders}`}
                                      />
                                    )}
                                    {row.partial_orders > 0 && (
                                      <div
                                        className="bg-yellow-400"
                                        style={{ width: `${(row.partial_orders / totalPayment) * 100}%` }}
                                        title={`Partial: ${row.partial_orders}`}
                                      />
                                    )}
                                  </div>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {row.prepaid_orders > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium">
                                        {row.prepaid_orders} Pre
                                      </span>
                                    )}
                                    {row.cod_orders > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 font-medium">
                                        {row.cod_orders} COD
                                      </span>
                                    )}
                                    {row.partial_orders > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 font-medium">
                                        {row.partial_orders} Part
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Disposition Breakdown ─────────────────────────────────────── */}
          {activeTab === 'disposition' && (
            <div className="p-6 space-y-8">
              {tabLoading['disposition'] ? (
                <div className="flex items-center justify-center h-48">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor" />
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading dispositions…</p>
                  </div>
                </div>
              ) : disposition.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400">No disposition data.</div>
              ) : (
                <>
                  {/* Per-agent disposition accordion */}
                  {agentDispositionTable.map((agent, agentIdx) => {
                    const isOpen = expandedAgentId === agent.user_id;
                    const maxCount = Math.max(...agent.rows.map(r => r.lead_count), 1);
                    return (
                      <div key={agent.user_id} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {/* Agent header — clickable */}
                        <button
                          onClick={() => setExpandedAgentId(isOpen ? null : agent.user_id)}
                          className="w-full bg-gray-50 dark:bg-gray-800/60 px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: PALETTE[agentIdx % PALETTE.length] }}
                            >
                              {agent.agent_name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-semibold text-gray-800 dark:text-white">{agent.agent_name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{agent.total} leads</span>
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        {/* Disposition rows — only when open */}
                        {isOpen && (
                          <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {agent.rows.map((row) => {
                              const pct = Math.round((row.lead_count / maxCount) * 100);
                              const sharePct = Math.round((row.lead_count / agent.total) * 100);
                              return (
                                <div key={row.disposition} className="px-4 py-2.5 flex items-center gap-3">
                                  <div
                                    className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                                    style={{ backgroundColor: PALETTE[row.colorIdx % PALETTE.length] }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{row.disposition}</span>
                                        {(row.l2_label || row.l3_label) && (
                                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                            ({row.l1_label})
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{sharePct}%</span>
                                        <span className="text-xs font-semibold text-gray-800 dark:text-white w-8 text-right">{row.lead_count}</span>
                                      </div>
                                    </div>
                                    <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full transition-all"
                                        style={{ width: `${pct}%`, backgroundColor: PALETTE[row.colorIdx % PALETTE.length] }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* ── Products ─────────────────────────────────────────────────── */}
          {activeTab === 'products' && (
            <div className="p-6 space-y-6">
              {/* Controls */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  <button
                    onClick={() => setProductMode('units')}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${productMode === 'units' ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    Units
                  </button>
                  <button
                    onClick={() => setProductMode('revenue')}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${productMode === 'revenue' ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    Revenue
                  </button>
                </div>
                <select
                  value={productAgentUuid}
                  onChange={e => setProductAgentUuid(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">All Agents</option>
                  {leaderboard.map(a => (
                    <option key={a.user_uuid} value={a.user_uuid}>{a.agent_name}</option>
                  ))}
                </select>
              </div>

              {tabLoading['products'] ? (
                <div className="flex items-center justify-center h-48">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor" />
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading products…</p>
                  </div>
                </div>
              ) : products.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400">No product data.</div>
              ) : (
                <>
                  {/* Bar chart */}
                  <div className="bg-gray-50 dark:bg-gray-800/40 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      Top 10 Products by {productMode === 'units' ? 'Units Sold' : 'Revenue'}
                    </h3>
                    <div style={{ height: Math.max(280, top10Products.length * 36) }}>
                      <Bar
                        data={productsChartData}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: ctx => productMode === 'revenue'
                                  ? ` ${fmt(ctx.parsed.x ?? 0)}`
                                  : ` ${ctx.parsed.x ?? 0} units`,
                              },
                            },
                          },
                          scales: {
                            x: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 11 } } },
                            y: { grid: { display: false }, ticks: { font: { size: 11 } } },
                          },
                        }}
                      />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">#</th>
                          <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Product</th>
                          <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Pack / Variant</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Orders</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Units</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {[...products]
                          .sort((a, b) => productMode === 'units'
                            ? b.total_units - a.total_units
                            : b.total_revenue - a.total_revenue)
                          .map((p, idx) => (
                            <tr key={`${p.product_title}-${p.pack_type}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                              <td className="px-4 py-3 text-gray-400 text-xs font-mono">{idx + 1}</td>
                              <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{p.product_title}</td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-medium">
                                  {p.pack_type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 font-mono">{p.order_count}</td>
                              <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 font-mono font-semibold">{p.total_units.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-gray-900 dark:text-white font-medium">{fmt(p.total_revenue)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Agent vs Product Matrix */}
                  {productMatrix.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Agent × Top Products
                        <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">
                          — who sells what the most (top 5 per agent, by units)
                        </span>
                      </h3>
                      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wider">
                            <tr>
                              <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Agent</th>
                              <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">#1 Product</th>
                              <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Units</th>
                              <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">#2 Product</th>
                              <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Units</th>
                              <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">#3 Product</th>
                              <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Units</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {productMatrix.map((agent, idx) => (
                              <tr key={agent.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                      style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}
                                    >
                                      {agent.agent_name.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="font-medium text-gray-900 dark:text-white">{agent.agent_name}</span>
                                  </div>
                                </td>
                                {[0, 1, 2].map(rank => {
                                  const p = agent.products[rank];
                                  return p ? (
                                    <>
                                      <td key={`${rank}-title`} className="px-4 py-3 max-w-[180px]">
                                        <div className="text-xs text-gray-800 dark:text-gray-200 truncate">{p.product_title}</div>
                                        {p.pack_type !== 'Default' && (
                                          <span className="text-xs text-purple-600 dark:text-purple-400">{p.pack_type}</span>
                                        )}
                                      </td>
                                      <td key={`${rank}-units`} className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                                        {p.total_units.toLocaleString()}
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td key={`${rank}-title`} className="px-4 py-3 text-gray-300 dark:text-gray-600 text-xs">—</td>
                                      <td key={`${rank}-units`} className="px-4 py-3" />
                                    </>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Response Time ─────────────────────────────────────────────── */}
          {activeTab === 'response' && (
            <div className="p-6 space-y-6">
              {tabLoading['response'] ? (
                <div className="flex items-center justify-center h-48">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor" />
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading response times…</p>
                  </div>
                </div>
              ) : responseTime.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400">No response time data.</div>
              ) : (
                <>
                  {/* How response time is calculated */}
                  <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <svg className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                      <p className="font-semibold mb-0.5">How response time is calculated</p>
                      <p className="text-blue-700 dark:text-blue-400 leading-relaxed">
                        <strong>Start time:</strong> For <em>abandoned cart</em> leads — the cart abandonment timestamp (<code>checkout_date</code>).
                        For all other leads — the lead creation time (<code>created_date</code>).
                        {' '}<strong>End time:</strong> The agent's <em>first non-system remark</em> (call note / audio / manual remark) on that lead.
                        {' '}<strong>Response time</strong> = end − start (in minutes). Leads with no agent remark are counted as "No Response".
                        Only positive values are counted — remarks entered before the lead start time are excluded.
                      </p>
                    </div>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">Avg Response (All)</p>
                      <p className={`text-2xl font-bold ${responseColor(avgResponseAll)}`}>
                        {fmtMins(avgResponseAll)}
                      </p>
                      <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">across all agents</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                      <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-1">Leads Responded</p>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-400">{respondedPct}%</p>
                      <p className="text-xs text-green-500 dark:text-green-400 mt-1">of assigned leads</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1">Fastest Agent</p>
                      <p className="text-lg font-bold text-purple-700 dark:text-purple-400 truncate">
                        {sortedResponse[0]?.agent_name ?? '—'}
                      </p>
                      <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                        {fmtMins(sortedResponse[0]?.avg_response_mins ?? null)} avg
                      </p>
                    </div>
                  </div>

                  {/* Color legend */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-sm bg-green-500" />
                      <span>&lt;30 min — Fast</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-sm bg-yellow-400" />
                      <span>30–120 min — Moderate</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-sm bg-red-500" />
                      <span>&gt;120 min — Slow</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-sm bg-gray-300" />
                      <span>No response</span>
                    </div>
                  </div>

                  {/* Bar chart */}
                  <div className="bg-gray-50 dark:bg-gray-800/40 rounded-xl p-4">
                    <div style={{ height: 280 }}>
                      <Bar
                        data={responseChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: { label: ctx => ` ${fmtMins(ctx.parsed.y)} avg response` },
                            },
                          },
                          scales: {
                            x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                            y: {
                              grid: { color: 'rgba(0,0,0,0.05)' },
                              ticks: {
                                font: { size: 11 },
                                callback: v => fmtMins(v as number),
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Agent</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Leads</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Responded</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">No Response</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Avg</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Min</th>
                          <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Max</th>
                          <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 min-w-[120px]">Response Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {sortedResponse.map((row, idx) => {
                          const rate = row.total_leads > 0
                            ? Math.round((row.leads_responded / row.total_leads) * 100)
                            : 0;
                          return (
                            <tr key={row.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                              <td className="px-4 py-3.5">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                    style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}
                                  >
                                    {row.agent_name.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="font-medium text-gray-900 dark:text-white">{row.agent_name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3.5 text-right text-gray-700 dark:text-gray-300 font-mono">{row.total_leads}</td>
                              <td className="px-4 py-3.5 text-right text-green-600 dark:text-green-400 font-mono">{row.leads_responded}</td>
                              <td className="px-4 py-3.5 text-right text-red-500 dark:text-red-400 font-mono">{row.leads_no_response}</td>
                              <td className={`px-4 py-3.5 text-right font-semibold font-mono ${responseColor(row.avg_response_mins)}`}>
                                {fmtMins(row.avg_response_mins)}
                              </td>
                              <td className="px-4 py-3.5 text-right text-gray-500 dark:text-gray-400 text-xs font-mono">
                                {fmtMins(row.min_response_mins)}
                              </td>
                              <td className="px-4 py-3.5 text-right text-gray-500 dark:text-gray-400 text-xs font-mono">
                                {fmtMins(row.max_response_mins)}
                              </td>
                              <td className="px-4 py-3.5">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                    <div
                                      className="h-full rounded-full transition-all"
                                      style={{
                                        width: `${rate}%`,
                                        backgroundColor: rate >= 80 ? '#22c55e' : rate >= 50 ? '#eab308' : '#ef4444',
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-600 dark:text-gray-400 w-8 text-right">{rate}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentPerformancePage;
